import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Chart, registerables } from 'chart.js';
import { filter, Observable } from 'rxjs';
import { parseDayString } from '../../../../../shared/helper/datehelper';
import {
  AttendanceRegularityQueryModel,
  AttendanceRegularityModel,
} from '../../../../../shared/store/attendance/user-work-attendance/types';
import { UserWorkAttendanceService } from '../../../../../shared/store/attendance/user-work-attendance/user-work-attendance.service';
import {
  AttendanceChart,
  toDurationLabel,
  toTimeLabel,
} from '../shared/attendance-chart';

/**
 * "Présence de l'équipe": one day, one salesperson per row. Rows and indicators come computed from
 * GET api/userAttendances/regularity; the subheader owns the selection. A row opens that salesperson's
 * dashboard.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-work-attendance-dashboard',
  standalone: false,
  templateUrl: './user-work-attendance-dashboard.component.html',
  styleUrl: './user-work-attendance-dashboard.component.scss',
})
export class UserWorkAttendanceDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('chartCanvas', { static: true })
  chartCanvas: ElementRef<HTMLCanvasElement>;

  presenceCount = signal('—');
  medianStart = signal('—');
  medianEnd = signal('—');
  medianAmplitude = signal('—');

  day = signal<Date | null>(null);
  hasRows = signal(false);
  isWorkingDay = signal(true);
  hasFormerEmployees = signal(false);
  chartHeightPx = signal(0);

  teamRegularity$: Observable<AttendanceRegularityModel>;
  teamRegularityError$: Observable<HttpErrorResponse | null>;

  private chart: AttendanceChart;
  /** Salesperson of each drawn row, by row index. */
  private rowUserIds: number[] = [];

  constructor(
    private readonly userWorkAttendanceService: UserWorkAttendanceService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
  ) {
    this.teamRegularity$ = this.userWorkAttendanceService.getRegularity.value$;
    this.teamRegularityError$ =
      this.userWorkAttendanceService.getRegularity.error$;
  }

  ngOnInit(): void {
    Chart.register(...registerables);

    this.chart = new AttendanceChart(
      () => this.translateService.instant('ATTENDANCE_LBL_WORK_SCHEDULE'),
      (index) => this.onRowClick(index),
    );

    this.teamRegularity$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((payload) => this.render(payload));

    // A failed call keeps the previous value in the slice: never leave the previous day drawn.
    this.teamRegularityError$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe(() => this.clear());
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.userWorkAttendanceService.getRegularity.reset();
  }

  onQueryChange(query: AttendanceRegularityQueryModel): void {
    this.userWorkAttendanceService.getRegularity.call(query);
  }

  private onRowClick(index: number): void {
    this.router.navigate([
      '/Crm/SalesPersonAttendanceDashboard',
      this.rowUserIds[index],
    ]);
  }

  private render(payload: AttendanceRegularityModel): void {
    // Taken from the payload, not the query: the title always names the day drawn.
    this.day.set(parseDayString(payload.from));
    this.presenceCount.set(`${payload.presenceCount}`);
    this.medianStart.set(toTimeLabel(payload.medianStartMinutes));
    this.medianEnd.set(toTimeLabel(payload.medianEndMinutes));
    this.medianAmplitude.set(toDurationLabel(payload.medianAmplitudeMinutes));

    // A row without presence stays, with an empty bar: the absence is what the screen shows.
    const dayRows = payload.users.map((user) => ({
      label: user.userContactName,
      day: user.days.find((x) => x.date === payload.from),
      muted: user.isFormerEmployee,
    }));
    // The back returns every working day of each user: nobody holding the day = weekend or public holiday,
    // not an absence of the whole team.
    const isWorkingDay = !dayRows.length || dayRows.some((x) => !!x.day);
    const rows = isWorkingDay ? dayRows : [];

    this.isWorkingDay.set(isWorkingDay);
    this.rowUserIds = isWorkingDay ? payload.users.map((x) => x.userId) : [];
    this.chart.render(this.chartCanvas.nativeElement, rows);
    this.chartHeightPx.set(this.chart.heightPx);
    this.hasRows.set(rows.length > 0);
    this.hasFormerEmployees.set(rows.some((x) => x.muted));
  }

  private clear(): void {
    this.day.set(null);
    this.presenceCount.set('—');
    this.medianStart.set('—');
    this.medianEnd.set('—');
    this.medianAmplitude.set('—');

    this.rowUserIds = [];
    this.chart.render(this.chartCanvas.nativeElement, []);
    this.chartHeightPx.set(0);
    this.hasRows.set(false);
    this.isWorkingDay.set(true);
    this.hasFormerEmployees.set(false);
  }
}
